import { MIN_CHUNK_SIZE } from "./chunkSize";
import type { IsobmffLayout, IsobmffLayoutResult } from "./isobmffLayout";

/**
 * The byte-offset manifest the `/uploads` facade requires to mint a `chunked-object` attempt
 * (spec 2026-09-04 §Transport step 2; server validator `validateChunkManifest` in
 * apps/contact-api/src/helpers/chunkedObjectManifest.ts). It is deliberately a PURE function of
 * `(fileSize, chunkSize)`: the same two numbers produce the same manifest on every device, so a
 * reloaded tab — which has no File, only the persisted row's size and chunk size — can rebuild the
 * manifest a re-mint needs, and the server's `.part` objects (sliced the same way) still line up.
 *
 * `digest` is intentionally absent (plan 3a decision D2): the web platform has no MD5, and the
 * server's per-part Size + single-PUT ETag checks plus the assembly ETag chain remain in force.
 */

/** Mirrors the server's MAX_TOTAL_CHUNKS / MAX_CHUNKED_OBJECT_PARTS; must be pinned by a cross-app guard (owed: plan 3a Task 6). */
export const MAX_CHUNKED_OBJECT_PARTS = 10_000;
/** `window.env` key; the exact string "false" disables OFFERING the transport. Absent = offer. Never a runtimeConfig requiredKey. */
export const CHUNKED_OBJECT_OFFER_KILL_SWITCH_KEY = "VITE_UPLOADS_OFFER_CHUNKED_OBJECT";

export interface ChunkManifestPart {
	ordinal: number;
	offset: number;
	length: number;
}

export interface ChunkManifest {
	totalBytes: number;
	parts: ChunkManifestPart[];
}

export type ChunkManifestBuildResult =
	| { ok: true; manifest: ChunkManifest }
	| {
			ok: false;
			reason: "empty" | "too-many-parts" | "chunk-below-floor" | "unsafe-size";
	  };

export function buildChunkManifest(fileSize: number, chunkSize: number): ChunkManifestBuildResult {
	if (!Number.isSafeInteger(fileSize) || !Number.isSafeInteger(chunkSize)) return { ok: false, reason: "unsafe-size" };
	if (fileSize <= 0) return { ok: false, reason: "empty" };
	// The S3 non-last-part floor (DL-1): a chunk below it would make the server's assembly fail with
	// EntityTooSmall, so the manifest is refused here rather than minted and then declined.
	if (chunkSize < MIN_CHUNK_SIZE) return { ok: false, reason: "chunk-below-floor" };
	const partCount = Math.ceil(fileSize / chunkSize);
	if (partCount > MAX_CHUNKED_OBJECT_PARTS) return { ok: false, reason: "too-many-parts" };
	const parts: ChunkManifestPart[] = [];
	for (let index = 0; index < partCount; index++) {
		const offset = index * chunkSize;
		parts.push({
			ordinal: index + 1,
			offset,
			length: Math.min(chunkSize, fileSize - offset)
		});
	}
	return { ok: true, manifest: { totalBytes: fileSize, parts } };
}

function chunkIndexesOverlapping(span: { start: number; end: number }, chunkSize: number, totalChunks: number): number[] {
	if (span.end <= span.start) return [];
	const first = Math.max(0, Math.floor(span.start / chunkSize));
	const last = Math.min(totalChunks - 1, Math.floor((span.end - 1) / chunkSize));
	const indexes: number[] = [];
	for (let index = first; index <= last; index++) indexes.push(index);
	return indexes;
}

/**
 * Header-first scheduling (spec §Transport step 4). The parts covering `ftyp`, the first `mdat`
 * header and `moov` go first so the server can locate the header layout and plan segments while
 * the body is still arriving. It is a PREFERENCE: the server treats a late moov as normal.
 * Returns a full permutation of `0..totalChunks-1` (priority chunks ascending, then the rest ascending).
 */
export function headerFirstChunkOrder(
	layout: Pick<IsobmffLayout, "ftyp" | "moov" | "mdat">,
	chunkSize: number,
	totalChunks: number
): number[] {
	const priority = new Set<number>([
		...chunkIndexesOverlapping(layout.ftyp, chunkSize, totalChunks),
		...chunkIndexesOverlapping({ start: layout.mdat.start, end: layout.mdat.headerEnd }, chunkSize, totalChunks),
		...chunkIndexesOverlapping(layout.moov, chunkSize, totalChunks)
	]);
	const ordered = [...priority].sort((left, right) => left - right);
	for (let index = 0; index < totalChunks; index++) if (!priority.has(index)) ordered.push(index);
	return ordered;
}

/** True when the runtime environment says NOT to offer the transport. Only the exact string "false" counts. */
export function readChunkedObjectOfferKillSwitch(env: unknown): boolean {
	try {
		if (!env || typeof env !== "object") return false;
		return (env as Record<string, unknown>)[CHUNKED_OBJECT_OFFER_KILL_SWITCH_KEY] === "false";
	} catch {
		return false;
	}
}

export function shouldOfferChunkedObject(input: {
	killSwitchDisabled: boolean;
	layout: IsobmffLayoutResult;
	manifest: ChunkManifestBuildResult;
}): { offer: true; manifest: ChunkManifest; layout: IsobmffLayout } | { offer: false; reason: string } {
	if (input.killSwitchDisabled) return { offer: false, reason: "kill-switch" };
	if (!input.layout.ok) return { offer: false, reason: `layout:${input.layout.reason}` };
	if (!input.manifest.ok) return { offer: false, reason: `manifest:${input.manifest.reason}` };
	return {
		offer: true,
		manifest: input.manifest.manifest,
		layout: input.layout.layout
	};
}
